import { Crown, GlobeHemisphereWest } from '@phosphor-icons/react';

import type { Appointment, WeeklyCalendarLabels } from './types';

interface AppointmentDetailsPanelProps {
  appointment: Appointment;
  staffName?: string;
  labels: WeeklyCalendarLabels;
}

/**
 * Read-only, presentational details panel. Rendered inside a SideSheet owned
 * by a smart wrapper; kept props-only so the same panel can be reused from
 * list views, search results, etc.
 */
export function AppointmentDetailsPanel({
  appointment,
  staffName,
  labels,
}: AppointmentDetailsPanelProps): JSX.Element {
  const fmt = (d: Date) =>
    `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {appointment.isVip && (
          <span className="inline-flex items-center gap-1 rounded-full bg-biz-accent-subtle px-2 py-1 text-xs font-semibold text-biz-accent">
            <Crown size={12} weight="regular" /> {labels.vipBadge}
          </span>
        )}
        {appointment.source === 'marketplace' && (
          <span className="inline-flex items-center gap-1 rounded-full bg-biz-info-subtle px-2 py-1 text-xs font-semibold text-biz-info">
            <GlobeHemisphereWest size={12} weight="regular" /> {labels.marketplaceBadge}
          </span>
        )}
        {appointment.syncAuthorityLocked && (
          <span className="inline-flex items-center rounded-full bg-biz-warning-subtle px-2 py-1 text-xs font-semibold text-biz-warning">
            {labels.syncAuthorityLockedLabel}
          </span>
        )}
        <span className="inline-flex items-center rounded-full bg-biz-muted px-2 py-1 text-xs font-medium text-biz-foreground-muted">
          {labels.statusLabels[appointment.status]}
        </span>
      </div>
      <div>
        <div className="text-biz-foreground-muted text-xs uppercase tracking-wider">
          {labels.petLabel}
        </div>
        <div className="text-2xl font-display font-semibold">{appointment.petName}</div>
        <div className="text-sm text-biz-foreground-muted">{appointment.customerName}</div>
      </div>
      <Row label={labels.serviceLabel} value={appointment.serviceName} />
      <Row
        label={labels.startLabel}
        value={`${appointment.startTime.toLocaleDateString()} · ${fmt(appointment.startTime)} – ${fmt(appointment.endTime)}`}
        mono
      />
      {staffName && <Row label={labels.staffLabel} value={staffName} />}
      {typeof appointment.price === 'number' && (
        <Row
          label={labels.priceLabel}
          value={`${appointment.price} ${appointment.currency ?? ''}`}
          mono
        />
      )}
      {appointment.source === 'marketplace' && (
        <Row label={labels.sourceLabel} value={labels.marketplaceBadge} />
      )}
      {appointment.externalBookingId && (
        <Row label={labels.externalBookingLabel} value={appointment.externalBookingId} mono />
      )}
      {appointment.syncAuthorityLocked && (
        <Row label={labels.syncAuthorityLockedLabel} value={labels.syncAuthorityLockedHelp} />
      )}
      {appointment.notes && <Row label={labels.notesLabel} value={appointment.notes} />}
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-biz-border last:border-b-0">
      <span className="text-xs font-semibold uppercase tracking-wider text-biz-foreground-muted">
        {label}
      </span>
      <span className={`text-sm text-right ${mono ? 'font-mono-num' : ''}`}>{value}</span>
    </div>
  );
}
